import React, { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * Mini-simulateur du barème parrainage N1 avec override « budget total sur la branche ».
 * Affiche pour un dossier de référence (10 000 € par défaut) les 3 cas de porteur :
 *   - N1 titulaire (override plein)
 *   - N2 en dessous (N1 = override − direct)
 *   - N3 en dessous (N1 = override − direct − n1)
 *
 * Props :
 *   epOverride, chOverride : valeurs saisies (%)
 *   epBase, chBase : { direct, n1, n2 } — barème global de base
 *   amountRef : montant de référence pour la démo (défaut 10 000 €)
 */
export function OverrideChainSimulator({
    epOverride,
    chOverride,
    epBase = { direct: 8, n1: 4, n2: 2 },
    chBase = { direct: 5, n1: 2, n2: 1 },
    amountRef = 10000,
}) {
    const scenarios = useMemo(() => {
        const _num = (v) => (v === '' || v === null || v === undefined || Number.isNaN(parseFloat(v))) ? null : parseFloat(v);
        const epOv = _num(epOverride);
        const chOv = _num(chOverride);
        const hasEp = epOv != null;
        const hasCh = chOv != null;
        if (!hasEp && !hasCh) return null;

        const rate = (base, ov, distance) => {
            if (ov == null) {
                // Pas d'override sur ce module : taux global à cette position
                if (distance === 0) return base.direct;
                if (distance === 1) return base.n1;
                return base.n2;
            }
            // Override N1 : formule « restant »
            if (distance === 0) return ov;
            if (distance === 1) return Math.max(ov - base.direct, 0);
            return Math.max(ov - base.direct - base.n1, 0);
        };

        const eur = (pct) => (pct * amountRef / 100);

        return [
            {
                key: 'n1-porteur',
                label: 'Le N1 titulaire porte le dossier',
                sub: 'Il est Direct — il touche son override plein.',
                rows: [
                    { role: 'N1 (titulaire · Direct)', ep: rate(epBase, epOv, 0), ch: rate(chBase, chOv, 0), highlight: true },
                ],
            },
            {
                key: 'n2-porte',
                label: 'Un N2 sous le N1 porte le dossier',
                sub: 'Le N1 touche le restant de son enveloppe.',
                rows: [
                    { role: 'N2 (Direct)', ep: epBase.direct, ch: chBase.direct, muted: true },
                    { role: 'N1 (titulaire · +1 cran)', ep: rate(epBase, epOv, 1), ch: rate(chBase, chOv, 1), highlight: true },
                ],
            },
            {
                key: 'n3-porte',
                label: 'Un N3 sous le N2 porte le dossier',
                sub: 'Le N1 touche ce qui reste après N3 et N2.',
                rows: [
                    { role: 'N3 (Direct)', ep: epBase.direct, ch: chBase.direct, muted: true },
                    { role: 'N2 (+1 cran)', ep: epBase.n1, ch: chBase.n1, muted: true },
                    { role: 'N1 (titulaire · +2 crans)', ep: rate(epBase, epOv, 2), ch: rate(chBase, chOv, 2), highlight: true },
                ],
            },
        ].map(sc => ({
            ...sc,
            rows: sc.rows.map(r => ({
                ...r,
                epAmount: eur(r.ep),
                chAmount: eur(r.ch),
            })),
        }));
    }, [epOverride, chOverride, epBase, chBase, amountRef]);

    if (!scenarios) return null;

    const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

    return (
        <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-3 space-y-2.5" data-testid="override-chain-simulator">
            <div className="flex items-baseline gap-2 flex-wrap">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-purple-800">Simulateur — 3 cas de porteur</h5>
                <span className="text-[11px] text-slate-500">Sur un dossier de démonstration à {fmt(amountRef)}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {scenarios.map(sc => (
                    <div key={sc.key} className="rounded-lg border border-purple-100 bg-white p-2.5 space-y-1.5" data-testid={`simulator-${sc.key}`}>
                        <div>
                            <p className="text-[12px] font-semibold text-slate-800 leading-tight">{sc.label}</p>
                            <p className="text-[10px] text-slate-500 leading-tight">{sc.sub}</p>
                        </div>
                        <div className="space-y-1">
                            {sc.rows.map((r, i) => (
                                <div key={i} className={`flex items-center justify-between gap-1 rounded px-1.5 py-1 text-[11px] ${r.highlight ? 'bg-purple-100 text-purple-900 font-semibold' : (r.muted ? 'text-slate-500' : 'text-slate-700')}`}>
                                    <span className="truncate">{r.role}</span>
                                    <span className="flex items-center gap-1.5 shrink-0 tabular-nums">
                                        <span title="Courtage">{r.ep}%<span className="ml-1 text-[10px] font-normal opacity-70">({fmt(r.epAmount)})</span></span>
                                        <ArrowRight className="h-2.5 w-2.5 opacity-30" />
                                        <span title="Travaux d’optimisation">{r.ch}%<span className="ml-1 text-[10px] font-normal opacity-70">({fmt(r.chAmount)})</span></span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default OverrideChainSimulator;
